import { PeopleListingFragment } from '@/__generated__/graphql';
import Error404 from "@/errorPages/Error404";
import Error500 from "@/errorPages/Error500";
import { getPeoplePage } from "@api/graphql/queries";
import PeopleListing from "@cmsPages/PeopleListing";
import { initializeApollo } from "@lib/apolloClient";
import { getFrontendHostname, getTheme } from '@lib/config';
import type { GetServerSideProps, GetServerSidePropsContext, NextPage } from "next";


type PeoplePageProps = {
  data: {
    people: PeopleListingFragment
  };
  pageUrl: string;
  siteName: string;
  homepageUrl: string;
  errorCode?: number;
};

const PeoplePage: NextPage<PeoplePageProps> = (props: PeoplePageProps) => {

  if (props.errorCode === 500) {
    return <Error500 />;
  }
  else if (props.errorCode) {
    return <Error404 />;
  }
  return <PeopleListing pageUrl={props.pageUrl} {...props.data.people} />;
};

export default PeoplePage;

export const getServerSideProps: GetServerSideProps = async (context: GetServerSidePropsContext) => {
  const { res, req } = context;
  const theme = getTheme();
  const path = context.resolvedUrl;
  const hostname = getFrontendHostname();

  let pageUrl;
  if (req) {
    // Server side rendering
    pageUrl = 'https://' + hostname + req.url;
  } else {
    // Client side rendering
    pageUrl = window.location.href;
  }
  // Zoos don't have a people page. Only allow the ZSL URL so we don't create duplicate content.
  if (theme !== 'zsl') {
    res.statusCode = 404;
    return {
      props: {
        theme: theme,
        pageUrl: pageUrl,
        hostname: hostname,
        errorCode: 404
      }
    };
  }

  // Get current params for the query.
  const params = new URL(pageUrl).searchParams;
  // Page number
  const page = (Number.parseInt(params.get('page') || '0') - 1) || 0;
  // Tag filters.
  const dept = params.getAll('department');
  const flatDept = dept ? dept.map((value) => value.split(',')).flat() : null;

  const apolloClient = initializeApollo();
  const result = await apolloClient.query({
    query: getPeoplePage,
    variables: {
      page: Math.max(page, 0),
      filterDept: flatDept || null,
    }
  })
  .catch((e) => {
    console.error('[GraphQL] Our People query failed:', e);
    res.statusCode = 500;
    return {
      data: {
        route:{}
      },
      errorCode: 500
    };
  });

  return {
    props: {
      theme: theme,
      pageUrl: pageUrl,
      hostname: hostname,
      ...result
    },
  };
};
